import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { CategoryContext } from '../contexts/CategoryContext';
import { getMediaUrl } from '../utils/urlHelpers';
import { CarouselSkeleton, CategorySkeleton, ProductGridSkeleton, PosterSlideshowSkeleton } from '../components/Skeletons';
import { UserContext } from '../contexts/UserContext';
import '../styles/dashboard.css';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import NetworkToaster from '../components/NetworkToaster';
import CustomCarousel from '../components/CustomCarousel';
import PremiumCarousel from '../components/PremiumCarousel';
import ProductsGrid from '../components/ProductsGrid';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { handleLikeClick as likeHandler } from '../utils/likeHandler';
import { filterActivePromotions } from '../utils/promotionUtils';
import ProductService from '../services/productService';
import PosterSlideshow from '../components/PosterSlideshow';

const Dashboard = () => {
    const navigate = useNavigate();
    const { user: contextUser, redirectToLogin } = useContext(UserContext); // Renamed to avoid conflict
    const { categoryList, isLoading: isLoadingCategories } = useContext(CategoryContext);

    const [loggedInUser, setLoggedInUser] = useState(contextUser);
    const [products, setProducts] = useState([]);
    const [promotedProducts, setPromotedProducts] = useState([]);
    const [connectionProducts, setConnectionProducts] = useState([]);
    const [searchBasedProducts, setSearchBasedProducts] = useState([]);
    const [productsByProvince, setProductsByProvince] = useState([]);
    const [analyticsBasedProducts, setAnalyticsBasedProducts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [randomCategories, setRandomCategories] = useState([]);
    const [likedProducts, setLikedProducts] = useState([]);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [provincePage, setProvincePage] = useState(1);
    const [hasMoreProvince, setHasMoreProvince] = useState(true);

    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    
    // NetworkToaster state for network connection issues
    const [networkToasterVisible, setNetworkToasterVisible] = useState(false);
    const [networkToasterMessage, setNetworkToasterMessage] = useState('');
    
    // Auto-retry mechanism for network failures
    const retryAttemptsRef = useRef(0);
    const isRetryingRef = useRef(false);
    const retryTimeoutRef = useRef(null);
    
    const pageSize = 30; // Increased from 10 to 30 for better performance
    const DASHBOARD_CACHE_KEY = 'dashboard_products_cache_v1';
    const DASHBOARD_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

    const [loadingStates, setLoadingStates] = useState({
        campaigns: true,
        promoted: true,
        connections: true,
        searchBased: true,
        analyticsBased: true,
        provinceProducts: true,
        latestProducts: true,
    });
    const [loadingMoreProducts, setLoadingMoreProducts] = useState(false); // For "load more" actions
    
    // Track API errors separately from loading states
    const [apiErrors, setApiErrors] = useState({
        campaigns: false,
        promoted: false,
        connections: false,
        searchBased: false,
        analyticsBased: false,
        provinceProducts: false,
        latestProducts: false,
    });
    
    // Ref to track initial load completion
    const initialLoadDone = useRef(false);

    // --- Helper: Fetch Logged In User ---
    const fetchLoggedInUser = useCallback(async () => {
        if (contextUser) {
            setLoggedInUser(contextUser);
            return contextUser;
        }

        const token = Cookies.get('authToken');
        if (token) {
            setLoggedInUser(null);
            return null;
        }

        setLoggedInUser(null);
        return null;
    }, [contextUser]);

    // --- Data Fetching Functions ---
    const fetchCampaigns = useCallback(async () => {
        setLoadingStates(prev => ({ ...prev, campaigns: true }));
        setApiErrors(prev => ({ ...prev, campaigns: false }));
        
        // Cache configuration
        const CAMPAIGNS_CACHE_KEY = 'wennze_campaigns_cache_v2'; // Incremented version to invalidate old cache
        const CAMPAIGNS_CACHE_TTL = 60 * 60 * 1000; // 1 hour like mobile app
        
        try {
            // Try to get from cache first
            const cachedData = localStorage.getItem(CAMPAIGNS_CACHE_KEY);
            if (cachedData) {
                try {
                    const { data: cachedCampaigns, timestamp } = JSON.parse(cachedData);
                    const isValidCache = Date.now() - timestamp < CAMPAIGNS_CACHE_TTL;
                    
                    if (isValidCache && cachedCampaigns.length > 0) {
                        setCampaigns(cachedCampaigns);
                        setLoadingStates(prev => ({ ...prev, campaigns: false }));

                        // Start background refresh for next time
                        setTimeout(() => {
                            fetch(`${config.backendUrl}/campaigns/active/`)
                                .then(response => response.ok ? response.json() : [])
                                .then(data => {
                                    // Handle paginated response structure
                                    let campaignsArray = [];
                                    if (data.results && Array.isArray(data.results)) {
                                        campaignsArray = data.results;
                                    } else if (Array.isArray(data)) {
                                        campaignsArray = data;
                                    } else if (data.id) {
                                        campaignsArray = [data];
                                    }
                                    
                                    const formattedCampaigns = campaignsArray.map(campaign => {
                                        let imageUrl = '';
                                        if (campaign.images && campaign.images.length > 0) {
                                            imageUrl = campaign.images[0].image; // Use full URL directly
                                        }
                                        
                                        return {
                                            imageUrl: imageUrl,
                                            link: campaign.link,
                                            caption: { 
                                                title: campaign.title || 'Promotion', 
                                                text: `Sponsorisé par ${campaign.client_name || 'Wennze'}` 
                                            },
                                            ownerId: campaign.user
                                        };
                                    });
                                    
                                    // Update cache silently
                                    localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
                                        data: formattedCampaigns,
                                        timestamp: Date.now()
                                    }));
                                })
                                .catch(() => {}); // Silent background refresh
                        }, 100);
                        
                        return; // Return early with cached data
                    }
                } catch (cacheError) {

                }
            }

            // Fetch from network if no valid cache
            const response = await fetch(`${config.backendUrl}/campaigns/active/`);
            if (!response.ok) throw new Error('Failed to fetch campaigns');
            const data = await response.json();
            
            // Handle paginated response structure
            let campaignsArray = [];
            if (data.results && Array.isArray(data.results)) {
                // Paginated response
                campaignsArray = data.results;
            } else if (Array.isArray(data)) {
                // Direct array response
                campaignsArray = data;
            } else if (data.id) {
                // Single object response
                campaignsArray = [data];
            }
            
            const formattedCampaigns = campaignsArray.map(campaign => {
                // Get image URL - it's already a full URL from S3
                let imageUrl = '';
                if (campaign.images && campaign.images.length > 0) {
                    imageUrl = campaign.images[0].image; // Use the full URL directly
                }
                
                return {
                    imageUrl: imageUrl,
                    link: campaign.link,
                    caption: { 
                        title: campaign.title || 'Promotion', 
                        text: `Sponsorisé par ${campaign.client_name || 'Wennze'}` 
                    },
                    ownerId: campaign.user // Add owner id for navigation
                };
            });
            
            setCampaigns(formattedCampaigns);
            
            // Cache the results
            localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
                data: formattedCampaigns,
                timestamp: Date.now()
            }));

        } catch (error) {
            console.error('Error fetching campaigns:', error);
            
            // Fallback to stale cache if network fails
            const cachedData = localStorage.getItem(CAMPAIGNS_CACHE_KEY);
            if (cachedData) {
                try {
                    const { data: cachedCampaigns } = JSON.parse(cachedData);
                    setCampaigns(cachedCampaigns);

                } catch (cacheError) {
                    setCampaigns([]);
                }
            } else {
                setCampaigns([]);
            }
            setApiErrors(prev => ({ ...prev, campaigns: true }));
        } finally {
            setLoadingStates(prev => ({ ...prev, campaigns: false }));
        }
    }, []);

    const fetchPromotedProducts = useCallback(async () => {
        setLoadingStates(prev => ({ ...prev, promoted: true }));
        setApiErrors(prev => ({ ...prev, promoted: false }));
        
        // Cache configuration
        const PROMOTED_CACHE_KEY = 'wennze_promoted_products_cache';
        const PROMOTED_CACHE_TTL = 10 * 60 * 1000; // 10 minutes like mobile app
        
        try {
            // Try to get from cache first
            const cachedData = localStorage.getItem(PROMOTED_CACHE_KEY);
            if (cachedData) {
                try {
                    const { data: cachedPromoted, timestamp } = JSON.parse(cachedData);
                    const isValidCache = Date.now() - timestamp < PROMOTED_CACHE_TTL;
                    
                    if (isValidCache && Array.isArray(cachedPromoted)) {
                        // Filter cached promoted products for expired ones
                        const activeCachedPromoted = filterActivePromotions(cachedPromoted);
                        setPromotedProducts(activeCachedPromoted);
                        setLoadingStates(prev => ({ ...prev, promoted: false }));
                        
                        // Start background refresh for next time
                        setTimeout(() => {
                            fetch(`${config.backendUrl}/products/promoted/`)
                                .then(response => response.ok ? response.json() : [])
                                .then(data => {
                                    // Filter and update cache silently
                                    const activeData = filterActivePromotions(data || []);
                                    localStorage.setItem(PROMOTED_CACHE_KEY, JSON.stringify({
                                        data: activeData,
                                        timestamp: Date.now()
                                    }));
                                })
                                .catch(() => {}); // Silent background refresh
                        }, 100);
                        
                        return; // Return early with cached data
                    }
                } catch (cacheError) {

                }
            }

            // Fetch from network if no valid cache
            const response = await fetch(`${config.backendUrl}/products/promoted/`);
            if (!response.ok) throw new Error('Failed to fetch promoted products');
            const data = await response.json();
            const promotedData = data || [];
            setPromotedProducts(promotedData);
            
            // Cache the results
            localStorage.setItem(PROMOTED_CACHE_KEY, JSON.stringify({
                data: promotedData,
                timestamp: Date.now()
            }));

        } catch (error) {
            console.error('Error fetching promoted products:', error);
            
            // Fallback to stale cache if network fails
            const cachedData = localStorage.getItem(PROMOTED_CACHE_KEY);
            if (cachedData) {
                try {
                    const { data: cachedPromoted } = JSON.parse(cachedData);
                    setPromotedProducts(cachedPromoted || []);

                } catch (cacheError) {
                    setPromotedProducts([]);
                }
            } else {
                setPromotedProducts([]);
            }
            setApiErrors(prev => ({ ...prev, promoted: true }));
        } finally {
            setLoadingStates(prev => ({ ...prev, promoted: false }));
        }
    }, []);

    const fetchLatestProducts = useCallback(async (currentPage = 1, pSize = pageSize, options = { silent: false }) => {
        const { silent } = options;
        if (currentPage === 1 && !silent) {
            setLoadingStates(prev => ({ ...prev, latestProducts: true }));
            setApiErrors(prev => ({ ...prev, latestProducts: false }));
        }
        if (currentPage > 1) setLoadingMoreProducts(true);

        try {
            // Use ProductService which already implements memory/local caching and background refresh
            let result;
            
            try {
                result = await ProductService.getDashboardProductsCombined(currentPage, pSize);
            } catch (e) {
                // If getDashboardProductsCombined fails (which might require auth),
                // fall back to getAllProductsCombined which works for all users

                result = await ProductService.getAllProductsCombined('', '', currentPage, pSize);
                
                // Normalize data structure if needed
                if (result.categories && !result.products) {
                    result.products = result.categories.flatMap(cat => cat.products || []);
                }
            }

            const dataProducts = result.products || [];

            if (currentPage === 1) {
                setProducts(dataProducts);
                setPage(1);
            } else {
                setProducts(prev => [...prev, ...dataProducts].filter((p, i, self) => i === self.findIndex(t => t.id === p.id)));
            }

            setHasMore(result.hasMore !== undefined ? result.hasMore : false);

            // Persist initial page to sessionStorage for instant restores
            if (currentPage === 1) {
                try {
                    const cachePayload = {
                        timestamp: Date.now(),
                        products: dataProducts || [],
                        page: 1,
                        hasMore: result.hasMore !== undefined ? result.hasMore : false,
                    };
                    sessionStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(cachePayload));
                } catch (err) {
                    // ignore sessionStorage errors
                }
            }

            if (result.fromCache) {

            } else {

            }
        } catch (error) {
            console.error('Error fetching latest products (ProductService):', error);
            if (currentPage === 1) {
                setProducts([]);
                setApiErrors(prev => ({ ...prev, latestProducts: true }));
            }
            setHasMore(false);
        } finally {
            if (currentPage === 1 && !options.silent) {
                setLoadingStates(prev => ({ ...prev, latestProducts: false }));
            }
            setLoadingMoreProducts(false);
        }
    }, [pageSize]);

    const fetchConnectionProducts = useCallback(async () => {
        if (!loggedInUser) {
            setConnectionProducts([]);
            setLoadingStates(prev => ({ ...prev, connections: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, connections: true }));
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/products/connections/`, {
                headers: { Authorization: `Token ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch connection products');
            const data = await response.json();
            setConnectionProducts(data);
        } catch (error) {
            console.error('Error fetching connection products:', error);
            setConnectionProducts([]);
        } finally {
            setLoadingStates(prev => ({ ...prev, connections: false }));
        }
    }, [loggedInUser]);

    const fetchSearchBasedProducts = useCallback(async () => {
        if (!loggedInUser) {
            setSearchBasedProducts([]);
            setLoadingStates(prev => ({ ...prev, searchBased: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, searchBased: true }));
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/products/recommendations/search-based/`, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setSearchBasedProducts(data.products || []);
            } else {
                setSearchBasedProducts([]);
            }
        } catch (error) {
            console.error('Error fetching search-based recommendations:', error);
            setSearchBasedProducts([]);
        } finally {
            setLoadingStates(prev => ({ ...prev, searchBased: false }));
        }
    }, [loggedInUser]);

    const fetchAnalyticsBasedProducts = useCallback(async () => {
        if (!loggedInUser) {
            setAnalyticsBasedProducts([]);
            setLoadingStates(prev => ({ ...prev, analyticsBased: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, analyticsBased: true }));
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/products/recommendations/analytics-based/`, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setAnalyticsBasedProducts(data || []);
            } else {
                setAnalyticsBasedProducts([]);
            }
        } catch (error) {
            console.error('Error fetching analytics-based recommendations:', error);
            setAnalyticsBasedProducts([]);
        } finally {
            setLoadingStates(prev => ({ ...prev, analyticsBased: false }));
        }
    }, [loggedInUser]);

    const fetchProductsByUserProvince = useCallback(async (currentPage = 1, pSize = pageSize) => {
        if (!loggedInUser) {
            setProductsByProvince([]);
            setLoadingStates(prev => ({ ...prev, provinceProducts: false }));
            setHasMoreProvince(false);
            return;
        }
        // Only set provinceProducts loading state for initial load (page 1)
        if (currentPage === 1) {
            setLoadingStates(prev => ({ ...prev, provinceProducts: true }));
        }
        if (currentPage > 1) setLoadingMoreProducts(true); // Use the same "load more" indicator
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/products/all/user-province/?page=${currentPage}&page_size=${pSize}`, {
                headers: { Authorization: `Token ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch province products');
            const data = await response.json();
            if (currentPage === 1) {
                setProductsByProvince(data.results);
                setProvincePage(1); // Reset page counter
            } else {
                setProductsByProvince(prev => [...prev, ...data.results].filter((p, i, self) => i === self.findIndex(t => t.id === p.id)));
            }
            setHasMoreProvince(data.next !== null);

            if (currentPage === 1 && data.results.length === 0 && data.next === null) {
                setHasMoreProvince(false);
            }
        } catch (error) {
            console.error('Error fetching province products:', error);
            if (currentPage === 1) setProductsByProvince([]);
            setHasMoreProvince(false);
        } finally {
            // Only clear provinceProducts loading state for initial load
            if (currentPage === 1) {
                setLoadingStates(prev => ({ ...prev, provinceProducts: false }));
            }
            setLoadingMoreProducts(false);
        }
    }, [loggedInUser, pageSize]); // Depend on loggedInUser and pageSize

    // --- useEffect Hooks for Data Management ---

    // Initial data load (user-agnostic) and fetch user
    useEffect(() => {
        if (initialLoadDone.current) return; // Prevent re-running

        // Set all loading states to true and reset errors on initial mount
        setLoadingStates({
            campaigns: true,
            promoted: true,
            connections: true,
            searchBased: true,
            analyticsBased: true,
            provinceProducts: true,
            latestProducts: true,
        });
        setApiErrors({
            campaigns: false,
            promoted: false,
            connections: false,
            searchBased: false,
            analyticsBased: false,
            provinceProducts: false,
            latestProducts: false,
        });

        fetchLoggedInUser();
        fetchCampaigns();
        fetchPromotedProducts();

        // Try to restore cached dashboard products to avoid visible reload on navigation back
        let usedCache = false;
        try {
            const raw = sessionStorage.getItem(DASHBOARD_CACHE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.timestamp && (Date.now() - parsed.timestamp) < DASHBOARD_CACHE_TTL) {
                    setProducts(parsed.products || []);
                    setPage(parsed.page || 1);
                    setHasMore(typeof parsed.hasMore === 'boolean' ? parsed.hasMore : true);
                    // mark loading as finished for the latest products section
                    setLoadingStates(prev => ({ ...prev, latestProducts: false }));
                    usedCache = true;
                }
            }
        } catch (err) {
            // ignore cache errors
        }

        if (!usedCache) {
            fetchLatestProducts(1); // Fetch initial page
        } else {
            // Kick off a silent background refresh to update cache without flashing UI
            fetchLatestProducts(1, pageSize, { silent: true }).catch(() => {});
        }

        initialLoadDone.current = true;
    }, []); // Remove dependencies to prevent re-running

    // User-dependent data load
    useEffect(() => {
        if (loggedInUser) {
            fetchConnectionProducts();
            fetchSearchBasedProducts();
            fetchAnalyticsBasedProducts();
            fetchProductsByUserProvince(1); // Fetch initial page
            // Reset pagination when user changes
            setProvincePage(1);
            // Liked products and WebSocket will be handled in a separate useEffect below
        } else {
            // Clear user-specific data if user logs out
            setConnectionProducts([]);
            setSearchBasedProducts([]);
            setAnalyticsBasedProducts([]);
            setProductsByProvince([]);
            setLikedProducts([]);
            setProvincePage(1);
            setHasMoreProvince(false);
            // Set loading states to false and clear errors for user-specific sections
            setLoadingStates(prev => ({
                ...prev,
                connections: false,
                searchBased: false,
                analyticsBased: false,
                provinceProducts: false,
            }));
            setApiErrors(prev => ({
                ...prev,
                connections: false,
                searchBased: false,
                analyticsBased: false,
                provinceProducts: false,
            }));
        }
    }, [loggedInUser, fetchConnectionProducts, fetchSearchBasedProducts, fetchAnalyticsBasedProducts, fetchProductsByUserProvince]); // Add missing dependencies

    // Update loggedInUser state when contextUser changes
    useEffect(() => {
        setLoggedInUser(contextUser);
    }, [contextUser]);

    // Handle top categories — sorted by product count, top 8
    useEffect(() => {
        if (categoryList && categoryList.length > 0) {
            const sorted = [...categoryList].sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
            setRandomCategories(sorted.slice(0, 8));
        }
    }, [categoryList]);

    // Handle liked products and WebSocket
    useEffect(() => {
        if (loggedInUser && products.length > 0) { // Also check products to avoid running too early
            const liked = products.filter(product => product.liked_by && product.liked_by.includes(loggedInUser.id)).map(product => product.id);
            setLikedProducts(liked);
        }

        if (loggedInUser) {
            const socket = new WebSocket(`${config.websocketUrl}product_like/${loggedInUser.id}/`);
            socket.onmessage = (event) => {
                const notificationData = JSON.parse(event.data);
                setNotification({ message: notificationData.body, type: 'info', header: 'Notification' });
            };
            socket.onerror = (error) => {
                console.error('WebSocket Error:', error);
            };
            socket.onclose = (event) => {
                if (!event.wasClean) {
                    console.error('WebSocket closed unexpectedly. Code:', event.code, 'Reason:', event.reason);
                }
            };
            return () => {
                if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                    socket.close();
                }
            };
        }
    }, [products, loggedInUser]); // Re-run if products or loggedInUser changes

    // Auto-retry mechanism for failed API calls
    const retryFailedRequests = useCallback(async () => {
        if (isRetryingRef.current) return; // Prevent multiple simultaneous retries
        
        isRetryingRef.current = true;
        retryAttemptsRef.current += 1;
        
        try {
            // Retry failed API calls
            const retryPromises = [];
            
            if (apiErrors.campaigns) {
                retryPromises.push(fetchCampaigns());
            }
            if (apiErrors.promoted) {
                retryPromises.push(fetchPromotedProducts());
            }
            if (apiErrors.latestProducts) {
                retryPromises.push(fetchLatestProducts(1));
            }
            if (apiErrors.connections && loggedInUser) {
                retryPromises.push(fetchConnectionProducts());
            }
            if (apiErrors.searchBased && loggedInUser) {
                retryPromises.push(fetchSearchBasedProducts());
            }
            if (apiErrors.analyticsBased && loggedInUser) {
                retryPromises.push(fetchAnalyticsBasedProducts());
            }
            if (apiErrors.provinceProducts && loggedInUser) {
                retryPromises.push(fetchProductsByUserProvince(1));
            }
            
            // Execute all retries in parallel
            if (retryPromises.length > 0) {
                await Promise.allSettled(retryPromises);
            }
            
        } catch (error) {
            console.error('Retry attempt failed:', error);
        } finally {
            isRetryingRef.current = false;
        }
    }, [
        apiErrors, loggedInUser,
        fetchCampaigns, fetchPromotedProducts, fetchLatestProducts,
        fetchConnectionProducts, fetchSearchBasedProducts,
        fetchAnalyticsBasedProducts, fetchProductsByUserProvince
    ]);

    // Keep a ref so the useEffect can call the latest version without depending on it
    const retryFailedRequestsRef = useRef(retryFailedRequests);
    retryFailedRequestsRef.current = retryFailedRequests;

    // Show network toaster for actual API failures, not empty results
    useEffect(() => {
        const hasApiError = apiErrors.latestProducts || apiErrors.promoted || apiErrors.campaigns ||
                          apiErrors.connections || apiErrors.searchBased || apiErrors.analyticsBased ||
                          apiErrors.provinceProducts;
        
        // Only evaluate once all initial loads have settled
        const isStillLoading = loadingStates.latestProducts || loadingStates.promoted || loadingStates.campaigns;
        if (isStillLoading) return;

        if (hasApiError) {
            const attempts = retryAttemptsRef.current;
            if (attempts >= 3) {
                setNetworkToasterMessage('Connexion instable détectée. Appuyez pour actualiser.');
                setNetworkToasterVisible(true);
            } else if (!isRetryingRef.current) {
                setNetworkToasterMessage('Impossible de charger certains contenus. Nouvelle tentative automatique...');
                setNetworkToasterVisible(true);
                
                // Auto-retry with exponential backoff (max 3 attempts)
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                }
                const retryDelay = Math.min(2000 * Math.pow(2, attempts), 8000);
                retryTimeoutRef.current = setTimeout(() => {
                    retryFailedRequestsRef.current();
                }, retryDelay);
            }
        } else if (networkToasterVisible) {
            // Connection restored
            retryAttemptsRef.current = 0;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            setNetworkToasterMessage('Connexion rétablie');
            setTimeout(() => {
                setNetworkToasterVisible(false);
            }, 2000);
        }
    }, [apiErrors, loadingStates, networkToasterVisible]);

    // Cleanup retry timeout on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);

    // --- Event Handlers & Other Logic ---
    const handleCategoryClick = (categoryId, categoryName) => {
        navigate(`/products/category/${categoryId}/${encodeURIComponent(categoryName)}`);
    };

    const handleLikeClick = async (productId, isLikedByUser) => {
        await likeHandler({
            productId, isLikedByUser, loggedInUser,
            setLikedProducts, setProducts, // Pass relevant setters
            navigate, setNotification, redirectToLogin,
        });
    };

    const handleProductClick = (productUuid, event) => {  // Changed to productUuid, added event parameter
        // Add animation effect
        const card = event?.currentTarget;
        if (card) {
            card.classList.add('clicked');
            setTimeout(() => {
                card.classList.remove('clicked');
            }, 600);
        }
        navigate(`/products/view/${productUuid}`);
    };

    const handlePostAction = () => {
        if (!loggedInUser) {
            redirectToLogin('/add-post');
        } else {
            navigate('/add-post');
        }
    };

    const handleCampaignClick = (campaign) => {
        if (!loggedInUser) {
            // Navigate to login page for non-logged-in users
            redirectToLogin(window.location.pathname);
        } else if (campaign.ownerUuid || campaign.ownerId) {
            // Navigate to user's corner/shop
            navigate(`/user/corner/${campaign.ownerUuid || campaign.ownerId}/`);
        } else if (campaign.link) {
            // Fallback to external link
            window.open(campaign.link, '_blank');
        }
    };

    const fetchMoreLatestData = () => {
        if (!hasMore || loadingMoreProducts) return;

        const nextPage = page + 1;
        setPage(nextPage);
        fetchLatestProducts(nextPage);
    };

    const fetchMoreProvinceData = () => {
        if (!hasMoreProvince || loadingMoreProducts) return;

        const nextPage = provincePage + 1;
        setProvincePage(nextPage);
        fetchProductsByUserProvince(nextPage);
    };

    // Manual retry function for user-triggered retries
    const handleManualRetry = useCallback(() => {
        retryAttemptsRef.current = 0; // Reset retry count for manual retry
        retryFailedRequests();
    }, [retryFailedRequests]);

    // Scroll to top button (no changes needed here, assuming it works)
    // ...

    return (
        <div className="dashboard-page-container">
            {/* Campaigns Poster Slideshow */}
            {loadingStates.campaigns ? (
                <PosterSlideshowSkeleton />
            ) : (
                campaigns.length > 0 && (
                    <div className="slideshow-section">
                        <div className="section-header-container">
                            <h2 className="slideshow-section-title">Promotions Spéciales</h2>
                            <p className="slideshow-section-subtitle">Découvrez nos offres exclusives</p>
                        </div>
                        <PosterSlideshow
                            posters={campaigns}
                            autoplaySpeed={5000}
                            onClick={(link, poster) => {
                                try {
                                    // If poster has an ownerId, check login before navigating to user corner
                                    if (poster && poster.ownerId) {
                                        if (!loggedInUser) {
                                            // Store the intended destination and navigate to login
                                            sessionStorage.setItem('userCornerRedirectUrl', `/user/corner/${poster.ownerUuid || poster.ownerId}`);
                                            redirectToLogin(window.location.pathname);
                                            return;
                                        }

                                        navigate(`/user/corner/${poster.ownerUuid || poster.ownerId}`);
                                    } else if (link) {
                                        // Fallback to external link (no login required)

                                        window.open(link, '_blank');
                                    } else {

                                    }
                                } catch (error) {
                                    console.error('Error handling campaign click:', error);
                                    // Fallback: try to open link if available
                                    if (link) {
                                        window.open(link, '_blank');
                                    }
                                }
                            }}
                        />
                    </div>
                )
            )}

            {/* Premium Products Carousel */}
            {loadingStates.promoted ? (
                <CarouselSkeleton />
            ) : (
                promotedProducts && promotedProducts.length > 0 && (
                    <PremiumCarousel
                        products={promotedProducts}
                        onProductClick={handleProductClick}
                        handleLikeClick={handleLikeClick}
                        loggedInUser={loggedInUser}
                        likedProducts={likedProducts}
                        title="Produits Premium"
                        subtitle="Découvrez notre sélection exclusive de produits mis en avant"
                    />
                )
            )}

            {/* CTA Banner */}
            <section className="cta-banner">
                <div className="cta-content">
                    <div className="cta-header">
                        <span className="cta-emoji">{loggedInUser ? '🌟' : '🎉'}</span>
                        <h2 className="cta-title">
                            {loggedInUser ? `Bonjour ${loggedInUser.first_name} ${loggedInUser.last_name}!` : "Votre Marketplace Préférée"}
                        </h2>
                    </div>
                    <p className="cta-message">
                        {loggedInUser
                            ? "Transformez vos articles en opportunités! Publiez votre première annonce en quelques clics et rejoignez des milliers de vendeurs satisfaits."
                            : "Découvrez un monde d'opportunités! Achetez, vendez et échangez dans la plus grande communauté de votre région."}
                    </p>
                </div>
                <div className="cta-buttons">
                    <button className="cta-button primary-action" onClick={handlePostAction}>
                        {loggedInUser ? (<>📢 Publier une Annonce <span className="button-accent">→</span></>)
                            : (<>🔓 Se Connecter pour Publier <span className="button-accent">→</span></>)}
                    </button>
                </div>
            </section>

            {/* Connection Products Carousel (Only if loggedInUser) */}
            {loggedInUser && (
                loadingStates.connections ? (
                    <CarouselSkeleton />
                ) : connectionProducts && connectionProducts.length > 0 ? (
                    <div className="ad-section">
                        <h2>Produits de Vos Connexions</h2>
                        <CustomCarousel
                            products={connectionProducts}
                            onProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            loggedInUser={loggedInUser}
                            key={`connection-carousel-${connectionProducts.length}`}
                        />
                    </div>
                ) : null // Optionally show a "No products from connections" message
            )}

            {/* Top Categories Section */}
            {randomCategories && randomCategories.length > 0 && (
                <div className="top-categories-section">
                    <h2 className="top-categories-title">Top Catégories</h2>
                    {isLoadingCategories ? (
                        <CategorySkeleton />
                    ) : (
                        <div className="top-categories-grid">
                            {randomCategories.map((category, index) => {
                                const pastelColors = ['#E3F2FD','#FCE4EC','#E8F5E9','#FFF3E0','#F3E5F5','#E0F2F1','#FFFDE7','#F9FBE7'];
                                const textColors = ['#1976D2','#C2185B','#388E3C','#F57C00','#7B1FA2','#00897B','#FBC02D','#689F38'];
                                const colorIndex = index % pastelColors.length;
                                return (
                                    <div 
                                        key={category.id} 
                                        className="top-category-card" 
                                        style={{ backgroundColor: pastelColors[colorIndex] }}
                                        onClick={() => handleCategoryClick(category.id, category.name)}
                                    >
                                        <div className="top-category-initial-circle">
                                            <span className="top-category-initial" style={{ color: textColors[colorIndex] }}>
                                                {category.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <p className="top-category-name">{category.name}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Search Based Products (Only if loggedInUser) */}
            {loggedInUser && (
                loadingStates.searchBased ? (
                    <CarouselSkeleton />
                ) : searchBasedProducts && searchBasedProducts.length > 0 ? (
                    <div className="ad-section">
                        <h2>Basé sur Vos Recherches</h2>
                        <CustomCarousel
                            products={searchBasedProducts}
                            onProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            loggedInUser={loggedInUser}
                        />
                    </div>
                ) : null
            )}

            {/* Analytics Based Products (Only if loggedInUser) */}
            {loggedInUser && (
                loadingStates.analyticsBased ? (
                    <CarouselSkeleton />
                ) : analyticsBasedProducts && analyticsBasedProducts.length > 0 ? (
                    <div className="ad-section">
                        <h2>Recommandé pour Vous</h2>
                        <CustomCarousel
                            products={analyticsBasedProducts}
                            onProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            loggedInUser={loggedInUser}
                        />
                    </div>
                ) : null
            )}

            {/* Product Grids Section */}
            {loggedInUser ? (
                // Logged-in user view
                loadingStates.provinceProducts ? ( // Check loading state for province products first
                    <>
                        <div className="category-header"><h2>Produits Disponibles dans Votre Province</h2></div>
                        <ProductGridSkeleton />
                    </>
                ) : productsByProvince && productsByProvince.length > 0 ? (
                    <>
                        <div className="category-header">
                            <h2>Produits Disponibles dans Votre Province</h2>
                            <button className="view-more-button" onClick={() => navigate('/products/user-province')}>Tout Voir</button>
                        </div>
                        <ProductsGrid
                            products={productsByProvince}
                            likedProducts={likedProducts}
                            loggedInUser={loggedInUser}
                            handleProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            hasMore={hasMoreProvince}
                            fetchMoreData={fetchMoreProvinceData}
                            loadingMore={loadingMoreProducts}
                            campaigns={campaigns}
                            onCampaignPress={handleCampaignClick}
                        />
                    </>
                ) : (
                    // Fallback to "Voir les Derniers Produits" for logged-in user if no province products
                    <>
                        <div className="category-header">
                            <h2>Voir les Derniers Produits</h2>
                            <button className="view-more-button" onClick={() => navigate('/products/all')}>Tout Voir</button>
                        </div>
                        {loadingStates.latestProducts ? (
                            <ProductGridSkeleton />
                        ) : products && products.length > 0 ? (
                            <ProductsGrid
                                products={products}
                                likedProducts={likedProducts}
                                loggedInUser={loggedInUser}
                                handleProductClick={handleProductClick}
                                handleLikeClick={handleLikeClick}
                                hasMore={hasMore}
                                fetchMoreData={fetchMoreLatestData}
                                loadingMore={loadingMoreProducts}
                                campaigns={campaigns}
                                onCampaignPress={handleCampaignClick}
                            />
                        ) : (
                            !loadingStates.latestProducts && <p className="empty-state-message">Aucun produit à afficher pour le moment.</p>
                        )}
                    </>
                )
            ) : (
                // Logged-out user view: Only "Voir les Derniers Produits"
                <>
                    <div className="category-header">
                        <h2>Voir les Derniers Produits</h2>
                        {/* Always show "Tout Voir" button for non-logged users if there are products */}
                        {(products && products.length > 0) && (
                            <button className="view-more-button" onClick={() => navigate('/products/all')}>Tout Voir</button>
                        )}
                    </div>
                    {loadingStates.latestProducts ? (
                        <ProductGridSkeleton />
                    ) : products && products.length > 0 ? (
                        <ProductsGrid
                            products={products}
                            likedProducts={likedProducts}
                            loggedInUser={loggedInUser}
                            handleProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            hasMore={hasMore}
                            fetchMoreData={fetchMoreLatestData}
                            loadingMore={loadingMoreProducts}
                            campaigns={campaigns}
                            onCampaignPress={handleCampaignClick}
                        />
                    ) : (
                        !loadingStates.latestProducts && <p className="empty-state-message">Aucun produit à afficher pour le moment.</p>
                    )}
                </>
            )}

            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onConfirm={notification.onConfirm}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}

            {/* Network Connection Toaster */}
            <NetworkToaster
                message={networkToasterMessage}
                visible={networkToasterVisible}
                onClose={() => setNetworkToasterVisible(false)}
                onClick={
                    networkToasterMessage.includes('Appuyez pour actualiser')
                        ? () => window.location.reload()
                        : (networkToasterMessage.includes('instable') ? handleManualRetry : undefined)
                }
                type={networkToasterMessage.includes('rétablie') ? 'success' : 'error'}
                duration={networkToasterMessage.includes('rétablie') ? 2000 : 10000}
            />
            <ScrollToTopButton />
        </div>
    );
};

export default Dashboard;